import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DialogStoreEnums } from '@app/store/client-slices/dialog/dialog-store.enums';
import { DialogFacadeService } from '@app/components/shared/confirmation-dialog/confirm-dialog.facade.service';
import { ErrorMessageCodeEnum } from '@app/services/error-message/error-message-code.enum';
import { ErrorMessageUtilService } from '@app/services/error-message/error-message-util.service';
import { DialogStoreState } from '@app/store/client-slices/dialog/dialog-store-state';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { InputFormComponent } from '@app/components/shared/input-form/input-form.component';

import {
  Buttons,
  ConfirmationMessage,
  IDialogResult,
} from '@app/components/shared/confirmation-dialog/model/dialog-models';

@Component({
  selector: 'faxe-confirmation-dialog',
  templateUrl: './confirmation-dialog.component.html',
  styleUrls: ['./confirmation-dialog.component.scss'],
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButton,
    ReactiveFormsModule,
    InputFormComponent,
  ],
})
export class ConfirmationDialogComponent<R> implements OnInit, OnDestroy {
  public buttons = Buttons;
  formGroup: UntypedFormGroup = this.formBuilder.group({});
  private unsubscribe$ = new Subject<void>();
  private dialogSate: DialogStoreState;

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    public dialogRef: MatDialogRef<
      ConfirmationDialogComponent<R>,
      IDialogResult
    >,
    @Inject(MAT_DIALOG_DATA) public data: ConfirmationMessage,
    private facadeService: DialogFacadeService
  ) {
    this.dialogRef.disableClose = true;
  }

  ngOnInit(): void {
    this.dialogRef
      .keydownEvents()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.onPrimaryButton();
        }

        if (event.key === 'Escape') {
          this.onSecondaryButton();
        }
        if (event.key === 's' && event.ctrlKey) {
          event.preventDefault();
          this.onCtrlS();
        }
      });
    if (this.data?.correlationId) {
      this.facadeService
        .hamtaDialogStoreStatus()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((dialogState) => this.handleDialogState(dialogState));

      this.facadeService.initDialog();
      this.facadeService.openDialog();
    }

    this.formGroup.reset();
    this.formGroup.markAsUntouched();
  }

  handleDialogState(dialogState: DialogStoreState) {
    if (
      (dialogState.status === DialogStoreEnums.stangdialog ||
        dialogState.status === DialogStoreEnums.operationmisslyckades) &&
      this.data.correlationId === dialogState.correlationId
    ) {
      this.dialogSate = dialogState;
      console.log('confirmation dialog state change', { status: dialogState.status, correlationId: dialogState.correlationId });
      if (dialogState.errorMessageCodeEnum) {
        this.data.message = ErrorMessageUtilService.getErrorMessage({
          code: ErrorMessageCodeEnum.i000,
        }).text;
        this.data.primaryButton = 'Tillbaka';
      } else {
        this.dialogRef.close({
          button: 'close',
        });
      }
    }
  }

  onCtrlS(): void {
    if (this.data.secondaryButton === Buttons.avbryt) {
      this.close({
        button: Buttons.ctrlS,
        confirmationMessage: this.data,
        dialogStoreState: this.dialogSate,
        input: this.formGroup.controls.input.value,
      });
    }
  }

  onSecondaryButton(): void {
    this.close({
      button: this.data.secondaryButton,
      confirmationMessage: this.data,
      dialogStoreState: this.dialogSate,
    });
  }

  private syncAutofilledValues(): void {
    const form = document.getElementById(`${this.dialogRef.id}-form`);
    if (!form) {
      return;
    }
    for (const input of this.data.inputs ?? []) {
      const key = input.keyValuepair.key;
      const element = form.querySelector<HTMLInputElement>(
        `input[name="${CSS.escape(input.name || key)}"]`
      );
      const control = this.formGroup.get(key)?.get('value');
      if (element && control && element.value && control.value !== element.value) {
        control.setValue(element.value);
      }
    }
  }

  onPrimaryButton() {
    this.syncAutofilledValues();
    console.log('confirmation dialog primary button', { correlationId: this.data.correlationId, valid: !this.formGroup.invalid });
    if (this.data.useInput || this.data.inputs) {
      if (!this.formGroup.invalid) {
        const returMap: Map<string, string> = new Map<string, string>();

        if (this.data.inputs) {
          for (const input of this.data.inputs) {
            returMap.set(
              input.keyValuepair.key,
              this.formGroup.value[input.keyValuepair.key]?.value
            );
          }
        }

        this.close({
          button: this.data.primaryButton,
          confirmationMessage: this.data,
          dialogStoreState: this.dialogSate,
          input: this.formGroup.controls.input?.value?.value,
          inputMap: returMap,
        });
      } else {
        this.formGroup.markAllAsTouched();
      }
    } else {
      this.close({
        button: this.data.primaryButton,
        confirmationMessage: this.data,
        dialogStoreState: this.dialogSate,
      });
    }
  }

  onCustomAction(): void {
    if (this.data.customActionCallback) {
      this.data.customActionCallback();
    }
  }

  close(dialogResult: IDialogResult): void {
    this.dialogRef.close(dialogResult);
  }

  ngOnDestroy(): void {
    if (this.data?.correlationId) {
      this.facadeService.closeDialog();
    }
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
